import {
  getApiErrorCode,
  getApiErrorMessage,
  isRecord,
  unwrapApiData,
} from '@/shared/utils/apiResponse';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { ErroHttp } from '@/shared/utils/erroHttp';
import { reportError } from '@/shared/utils/reportError';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import { PRODUTO_PARA_CATEGORIA } from '../constants';
import type {
  ConsultaCnpjResultado,
  CriarContaEntrada,
  CriarContaPayload,
  CriarContaResposta,
  CriarContaResultado,
  TermosVigentes,
} from '../types';
import { MENSAGEM_FALHA_CRIACAO_CONTA } from '../utils/erroCriacaoConta';

const SKIP_AUTH = true;

const SOURCE = 'onboardingSelf.services';

const unwrap = <T>(response: Response, fallback: string): Promise<T> =>
  unwrapApiData<T>(response, { fallback, source: SOURCE });

const campo = (valor: unknown, chave: string): unknown =>
  isRecord(valor) ? valor[chave] : undefined;

function ehCriarContaResposta(valor: unknown): valor is CriarContaResposta {
  return (
    typeof campo(campo(valor, 'user'), 'id') === 'string' &&
    typeof campo(valor, 'espacoDeTrabalhoId') === 'string' &&
    typeof campo(campo(valor, 'presente'), 'saldoNotificacoes') === 'number' &&
    typeof campo(valor, 'sessaoCriada') === 'boolean'
  );
}

/**
 * Não usa `unwrapApiData`: ele descarta status e `error.code`, e a tela de erro da
 * criação decide a saída (login, nova tentativa, revisar) justamente por eles.
 */
async function lerCriacaoConta(response: Response): Promise<CriarContaResposta> {
  const corpo: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ErroHttp(
      response.status >= 500
        ? MENSAGEM_FALHA_CRIACAO_CONTA
        : getApiErrorMessage(corpo, MENSAGEM_FALHA_CRIACAO_CONTA),
      response.status,
      null,
      getApiErrorCode(corpo),
    );
  }
  const data = campo(corpo, 'data');
  if (!ehCriarContaResposta(data)) {
    // A conta pode ter sido criada: sem o report, o drift do 201 só aparece como "tente de novo".
    reportError(new Error('Resposta da criação de conta fora do contrato'), {
      source: SOURCE,
      status: response.status,
    });
    throw new ErroHttp(MENSAGEM_FALHA_CRIACAO_CONTA, response.status);
  }
  return data;
}

export const onboardingSelfService = {
  async carregarTermos(signal?: AbortSignal): Promise<TermosVigentes> {
    const response = await apiGet('/onboarding-self/termos', undefined, SKIP_AUTH, signal);
    return unwrap<TermosVigentes>(response, 'Erro ao carregar os Termos de Uso');
  },

  async consultarCnpj(cnpj: string, signal?: AbortSignal): Promise<ConsultaCnpjResultado> {
    const response = await apiPost(
      '/onboarding-self/cnpj',
      { cnpj: normalizeCpfCnpj(cnpj) },
      SKIP_AUTH,
      signal,
    );
    // 429 com status preservado: a tela separa o limite de consultas da Receita fora do ar.
    if (response.status === 429) throw new ErroHttp('Muitas consultas seguidas', 429);
    return unwrap<ConsultaCnpjResultado>(response, 'Erro ao consultar o CNPJ');
  },

  async criarConta(entrada: CriarContaEntrada, signal?: AbortSignal): Promise<CriarContaResultado> {
    const payload: CriarContaPayload = {
      cnpj: normalizeCpfCnpj(entrada.empresa.cnpj),
      responsavelNome: entrada.empresa.responsavelNome.trim(),
      responsavelEmail: entrada.empresa.responsavelEmail.trim(),
      produtosInteresse: entrada.produtos.map((id) => PRODUTO_PARA_CATEGORIA[id]),
      volumeCasosMes: entrada.volume,
      canalAquisicao: entrada.canal,
      aceiteTermos: true,
      termosVersao: entrada.termosVersao,
    };
    const response = await apiPost('/onboarding-self/contas', payload, SKIP_AUTH, signal);
    const data = await lerCriacaoConta(response);
    return {
      usuarioId: data.user.id,
      espacoDeTrabalhoId: data.espacoDeTrabalhoId,
      saldoPresente: data.presente.saldoNotificacoes,
      sessaoCriada: data.sessaoCriada,
    };
  },
};
